import { DragEvent, useRef, useState } from 'react'

interface DropZoneProps {
  /** Called with the dropped/picked files. */
  onFiles: (files: File[]) => void
  /** File input accept attribute (e.g. "image/*"). */
  accept?: string
  /** Allow selecting multiple files. */
  multiple?: boolean
  /** Main label shown inside the zone. */
  label: string
  /** Small hint under the label. */
  hint?: string
  /** Emoji/icon shown above the label. */
  icon?: string
  disabled?: boolean
}

/**
 * C7: reusable drag-and-drop file zone with a hidden <input type="file">
 * fallback (click to browse). Visual feedback while a file is dragged over.
 */
export default function DropZone({
  onFiles,
  accept,
  multiple = false,
  label,
  hint,
  icon = '📂',
  disabled = false,
}: DropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  const emit = (files: FileList | null) => {
    const arr = Array.from(files ?? [])
    if (arr.length > 0) onFiles(arr)
  }

  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    if (disabled) return
    emit(e.dataTransfer.files)
  }

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={label}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !disabled) inputRef.current?.click()
      }}
      onDragOver={(e) => {
        e.preventDefault()
        if (!disabled) setDragOver(true)
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer select-none ${
        disabled
          ? 'opacity-50 cursor-not-allowed border-gray-200 bg-gray-50'
          : dragOver
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 bg-white hover:border-gray-400'
      }`}
    >
      <div className="text-3xl mb-2">{icon}</div>
      <p className="text-gray-700 font-medium">{label}</p>
      {hint && <p className="text-gray-500 text-sm mt-1">{hint}</p>}
      <p className="text-gray-400 text-xs mt-2">Arrastra archivos aquí o haz clic para elegir</p>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          emit(e.target.files)
          e.target.value = '' // allow re-picking the same file
        }}
      />
    </div>
  )
}
